/**
 * Queue-entry refusal mapping (retest item 8b, droom half).
 *
 * `POST /queue:enter` 422s with a message NAMING exactly what is missing
 * (consent record / artefact kinds / an unsatisfied pre-call check). The
 * waiting room used to swallow that refusal (`void enterQueue().catch(()=>{})`)
 * and sit on "Finding your place in line…" forever — the be's honest answer
 * became an eternal skeleton. This module is the pure mapper from the
 * refusal's wording to the flow step that can FIX it, so the UI can render
 * the refusal and offer "Go back and complete".
 *
 * The mapping is deliberately conservative: it only ever routes to a step that
 * EXISTS in this session's (possibly plan-derived) flow order, and returns
 * null when nothing maps — the caller then shows the refusal with a plain
 * retry, never a route to a step the customer cannot reach.
 */

import type { StepId } from '@/types';

/** Candidate steps for a named lack, in preference order. */
function candidatesFor(message: string): readonly StepId[] {
  const m = message.toUpperCase();
  if (m.includes('CONSENT')) return ['consent'];
  if (m.includes('DIGILOCKER') || m.includes('AADHAAR')) {
    return ['check:DIGILOCKER_AADHAAR', 'aadhaar'];
  }
  if (m.includes('PAN')) return ['check:PAN_SOURCE', 'capture'];
  if (
    m.includes('ARTIFACT') ||
    m.includes('ARTEFACT') ||
    m.includes('DOC') ||
    m.includes('SELFIE') ||
    m.includes('PHOTO') ||
    m.includes('CAPTURE')
  ) {
    return ['capture'];
  }
  return [];
}

/**
 * The flow step that can repair this refusal, or null when the wording maps to
 * nothing reachable in `flowOrder` (unknown vocabulary / deploy skew).
 */
export function queueRefusalStep(
  message: string,
  flowOrder: readonly StepId[],
): StepId | null {
  for (const step of candidatesFor(message)) {
    if (flowOrder.includes(step)) return step;
  }
  return null;
}
